import { useMemo } from "react";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { getNurseEffectiveStatus } from "../utils/formatters";
import { buildStaffCountByJobId } from "../utils/jobs";
import { EffectiveEmployeeStatus, type Nurse } from "../utils/models";

/**
 * Whether an employee's jobs should shape this department's popularity order.
 *
 * Membership is read from `departments` rather than inferred from the status maps, because the two
 * can legitimately disagree. The demo-department branch of `read_nurses` borrows the roster from
 * `InHouse__Hospital_Demo` and re-homes `department_ids` onto the demo department, but leaves
 * `departmentStatuses` / `departmentEmployeeStatuses` keyed by the department it borrowed from — so
 * a demo roster has membership and no status row at all. Reading that as Inactive dropped every
 * employee, which silently disabled popularity ordering on exactly the departments demos are given.
 *
 * Membership is also what stops a stale roster skewing a different department: `refreshNursesData`
 * leaves `hospitalData.nurses` untouched when a refresh comes back empty or throws, so the array
 * can still describe the department we navigated away from. Employees who do not list this
 * department are excluded, which collapses that case to "no signal" and the alphabetical fallback.
 */
function countsTowardPopularity(nurse: Nurse, departmentId: string): boolean {
	if (!nurse.departments?.includes(departmentId)) return false;
	const hasStatusRow = nurse.departmentEmployeeStatuses?.[departmentId] !== undefined || nurse.departmentStatuses?.[departmentId] !== undefined;
	// DEFAULT: `true` for an employee this department claims but has no status row for. Unrepresented
	// is not terminated — only an explicit Inactive status, or an explicit `active: false`, excludes
	// someone. A missing row is a data gap (or the demo re-homing above) and must not quietly shrink
	// the counts; the alternative reads absence as a termination and undercounts real staff.
	if (!hasStatusRow) return true;
	return getNurseEffectiveStatus(nurse, departmentId) !== EffectiveEmployeeStatus.Inactive;
}

/**
 * How many of the chosen department's staff hold each job, keyed by job id — the popularity signal
 * behind `JobSelector`'s option order. Fully client-side: it derives from the roster
 * `HospitalDataProvider` has already fetched, so it costs no request.
 *
 * **Who counts.** Everyone whose effective status in the chosen department is anything other than
 * `Inactive`. Terminated staff drop out; On Leave and Suspended staff stay, because they still hold
 * the job and will work it again.
 *
 * Status comes from `getNurseEffectiveStatus`, the app's single source of truth: it prefers the
 * backend-computed `departmentEmployeeStatuses` map and falls back to the legacy `departmentStatuses`
 * boolean only when the chosen department has no computed entry. Calling `isNurseActiveInDepartment`
 * directly would skip the computed map altogether, so this is the better read.
 *
 * It is asked only about employees who have a status row at all, though. `getNurseEffectiveStatus`
 * resolves "absent from both maps" to Inactive, which is the right answer to *its* question and the
 * wrong one to this one — see {@link countsTowardPopularity} for why membership and status have to
 * be read separately.
 *
 * Returns `null` — never an empty map — when there is no signal at all: the roster has not loaded
 * yet, or nobody in the department holds a job. Callers must then decide what "no popularity" means
 * rather than accidentally sorting by an all-zero map.
 *
 * This subscribes the caller to `HospitalDataProvider`, whose context value is a fresh object
 * literal on every provider render. That is acceptable because nearly every `JobSelector` parent
 * already consumes that context, and the single `useMemo` is keyed on the `nurses` array identity —
 * stable across the many schedule-edit `setHospitalData` calls that spread `prevData` — so the
 * render body stays O(1) and both this map and the sorted array derived from it stay referentially
 * stable.
 */
export function useStaffCountByJobId(): ReadonlyMap<string, number> | null {
	const { hospitalData, chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment.department_id;

	return useMemo(() => {
		const nurses = hospitalData.nurses;
		if (!nurses?.length) return null;
		const employed = nurses.filter((nurse) => countsTowardPopularity(nurse, departmentId));
		const counts = buildStaffCountByJobId(employed);
		return counts.size ? counts : null;
	}, [hospitalData.nurses, departmentId]);
}
